import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import constants from "../constants/constants";
import { useLocation } from "react-router-dom";
import { faEnvelope, faPhone } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useState, useRef, useCallback } from "react";
import SEO, { pageSEO } from "../components/seo.component";
import { useUserParams } from "../hooks/useUserParams";

const useAnimatedBorder = () => {
  const topLeftRef = useRef(null);
  const topRightRef = useRef(null);
  const bottomLeftRef = useRef(null);
  const bottomRightRef = useRef(null);
  const rightRef = useRef(null);
  const leftRef = useRef(null);

  useEffect(() => {
    const timeouts = [];

    // Top borders animation
    timeouts.push(
      setTimeout(() => {
        topLeftRef.current?.classList.remove("w-0");
        topLeftRef.current?.classList.add("w-half");
        topRightRef.current?.classList.remove("w-0");
        topRightRef.current?.classList.add("w-half");
      }, 10)
    );

    // Side borders animation
    timeouts.push(
      setTimeout(() => {
        leftRef.current?.classList.remove("h-0");
        leftRef.current?.classList.add("h-full");
        rightRef.current?.classList.remove("h-0");
        rightRef.current?.classList.add("h-full");
      }, 1000)
    );

    // Bottom borders animation
    timeouts.push(
      setTimeout(() => {
        bottomLeftRef.current?.classList.remove("w-0");
        bottomLeftRef.current?.classList.add("w-half");
        bottomRightRef.current?.classList.remove("w-0");
        bottomRightRef.current?.classList.add("w-half");
      }, 4000)
    );

    return () => timeouts.forEach((timeout) => clearTimeout(timeout));
  }, []);

  return {
    topLeftRef,
    topRightRef,
    bottomLeftRef,
    bottomRightRef,
    rightRef,
    leftRef,
  };
};

const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState(window.innerWidth);

  const onWindowResize = useCallback(() => {
    setWindowSize(window.innerWidth);
  }, []);

  useEffect(() => {
    window.addEventListener("resize", onWindowResize);
    return () => window.removeEventListener("resize", onWindowResize);
  }, [onWindowResize]);

  return windowSize;
};

const useScrollVisibility = () => {
  const slideRefs = useRef([]);

  const addToRefs = (el) => {
    if (el && !slideRefs.current.includes(el)) {
      slideRefs.current.push(el);
    }
  };

  const isScrolledIntoView = useCallback((elem) => {
    if (elem) {
      const rect = elem.getBoundingClientRect();
      const elemTop = rect.top;
      const elemBottom = rect.bottom;
      return elemTop >= 0 && elemBottom <= window.innerHeight;
    }
    return false;
  }, []);

  useEffect(() => {
    slideRefs.current.forEach((el) => {
      if (isScrolledIntoView(el)) {
        el?.classList.remove("slide-left");
      }
    });
  }, [isScrolledIntoView]);

  return { addToRefs };
};

export default function About() {
  const {
    getCurrentAgentName,
    getCurrentAgentTitle,
    getCurrentAgentNumber,
    getCurrentAgentEmail,
    getCurrentBrokerage,
    getCurrentHeadshot,
    getCurrentAbout
  } = useUserParams();
  
  const windowSize = useWindowSize();
  const {
    topLeftRef,
    topRightRef,
    bottomLeftRef,
    bottomRightRef,
    rightRef,
    leftRef,
  } = useAnimatedBorder();
  const { addToRefs } = useScrollVisibility();

  const seoConfig = pageSEO.about();

  return (
    <>
      <SEO {...seoConfig} />
      <div className="max-w-[1100px] mx-auto gap-x-24 gap-y-10 px-6 grid sm:grid-cols-2 min-h-[70vh] pt-[150px] min-[1100px]:pt-[175px] mb-[100px]">
        <div className="relative self-start">
          {windowSize > 1200 ? (
            <div className="absolute h-[510px] w-[120%] bottom-[-30px] left-[-10%] -z-10">
              <div
                ref={topRightRef}
                className="bg-logo-main absolute w-0 left-[50%] h-[2px] duration-1000"
              ></div>
              <div
                ref={topLeftRef}
                className="bg-logo-main absolute w-0 right-[50%] h-[2px] duration-1000"
              ></div>
              <div
                ref={leftRef}
                className="bg-logo-main absolute w-[2px] left-0 h-0 duration-3000"
              ></div>
              <div
                ref={rightRef}
                className="bg-logo-main absolute w-[2px] right-0 h-0 duration-3000"
              ></div>
              <div
                ref={bottomRightRef}
                className="bg-logo-main absolute w-0 bottom-0 right-0 h-[2px] duration-1000"
              ></div>
              <div
                ref={bottomLeftRef}
                className="bg-logo-main absolute w-0 bottom-0 left-0 h-[2px] duration-1000"
              ></div>
            </div>
          ) : null}
          <img
            src={getCurrentHeadshot()}
            className="w-full"
            alt={getCurrentAgentName()}
          />
          <div className="flex flex-col gap-y-2 text-black mt-6">
            <h2 className="text-black libre text-3xl">
              {getCurrentAgentName()}
            </h2>
            <p className="italic text-lg">{getCurrentAgentTitle()}, {getCurrentBrokerage()}</p>

            <div className="flex gap-x-5 my-2">
              {Object.entries(constants?.SOCIALS ?? {})?.map(([k, v]) => (
                <a key={k} href={constants.SOCIALS[k]}>
                  <FontAwesomeIcon
                    className="fa-xl text-logo-third"
                    icon={constants.ICONS[k]}
                  />
                </a>
              ))}
            </div>
            <p className="font-semibold text-lg">
              <FontAwesomeIcon
                icon={faPhone}
                className="pr-2 text-logo-second"
              />
              {getCurrentAgentNumber()}{" "}
            </p>
            <p className="text-lg">
              <FontAwesomeIcon
                icon={faEnvelope}
                className="pr-2 text-logo-second"
              />
              {getCurrentAgentEmail()}{" "}
            </p>
          </div>
        </div>
        <div>
          <h1
            ref={addToRefs}
            className={`text-4xl text-black pb-8 libre duration-2000 slide-in ${
              windowSize > 700 ? "slide-left" : ""
            }`}
          >
            About Me
          </h1>

          {getCurrentAbout() ? (
            getCurrentAbout().split("<|>").map((p, index) => (
              <p key={index}>
                {p}
                <br />
                <br />
              </p>
            ))
          ) : (
            <p>Coming Soon</p>
          )}
        </div>
      </div>
    </>
  );
}
